import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiBaseUrl } from '../api/client';
import EventFallbackPattern from './EventFallbackPattern';
import Modal from './Modal';
import { EmptyState, SkeletonCard } from './ui';
import { PublicEventDetailContent } from '../pages/PublicEventPage';
import './EventMarquee.css';

interface EventMarqueeProps {
  /** Optional "See all" link href shown next to the title. */
  seeAllHref?: string;
  /** Optional "See all" link label (defaults to the translated "See all events"). */
  seeAllLabel?: string;
}

interface PublicEvent {
  _id: number;
  name: string;
  startTime: string;
  price: number | null;
  publicSlug: string;
  gallery?: string[];
  colorSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
}

const AUTO_CYCLE_MS = 5000;
const IMAGE_CYCLE_MS = 4000;
const TILE_WIDTH = 220;
const ACTIVE_SCALE = 1.2;
const ADJACENT_SCALE = 0.92;
const FAR_SCALE = 0.8;
const TILE_GAP = 24;

const getImageUrl = (path: string | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/uploads')) return `${getApiBaseUrl()}${path}`;
  return path;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ringOffset = (from: number, to: number, len: number): number => {
  const raw = to - from;
  if (raw > len / 2) return raw - len;
  if (raw < -len / 2) return raw + len;
  return raw;
};

// ---------------------------------------------------------------------------
// GalleryCycler – softly crossfades through gallery images inside a tile
// ---------------------------------------------------------------------------
const GalleryCycler: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, IMAGE_CYCLE_MS);
    return () => clearInterval(id);
  }, [images.length]);

  if (images.length === 1) {
    return <img src={images[0]} alt={alt} className="event-marquee__tile-image" loading="lazy" />;
  }

  return (
    <div className="event-marquee__tile-gallery">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === current ? alt : ''}
          className="event-marquee__tile-gallery-img"
          style={{ opacity: i === current ? 1 : 0 }}
          loading="lazy"
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EventMarquee
// ---------------------------------------------------------------------------
const EventMarquee: React.FC<EventMarqueeProps> = ({ seeAllHref, seeAllLabel }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [modalSlug, setModalSlug] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setFailed(false);
    api.publicEvents.list()
      .then((res) => {
        if (!cancelled) {
          setEvents(res.data as PublicEvent[]);
          setLoaded(true);
        }
      })
      .catch(() => {
        // Kept distinct from "no events": empty hides the section, a failure
        // must not, or an outage is indistinguishable from a quiet week.
        if (!cancelled) {
          setFailed(true);
          setLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % events.length);
  }, [events.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + events.length) % events.length);
  }, [events.length]);

  // Auto-cycle the carousel
  useEffect(() => {
    if (events.length <= 1 || paused) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % events.length);
    }, AUTO_CYCLE_MS);
    return () => clearInterval(id);
  }, [events.length, paused]);

  // Reserve the space instead of returning null: popping the section in after
  // the fetch shifts everything below it, right at the fold.
  if (!loaded) {
    return (
      <div className="event-marquee" aria-busy="true">
        <div className="event-marquee__header">
          <h2 className="event-marquee__title">{t('welcomePage.upcomingEvents')}</h2>
        </div>
        <div className="event-marquee__loading">
          <SkeletonCard />
        </div>
      </div>
    );
  }

  // A failed fetch always shows — see the catch above.
  if (failed) {
    return (
      <div className="event-marquee">
        <div className="event-marquee__header">
          <h2 className="event-marquee__title">{t('welcomePage.upcomingEvents')}</h2>
        </div>
        <div className="event-marquee__empty">
          <EmptyState
            size="sm"
            title={t('welcomePage.eventsLoadError')}
            action={{ label: t('common.retry'), onClick: () => setReloadKey((k) => k + 1) }}
          />
        </div>
      </div>
    );
  }

  // Genuinely nothing scheduled: render nothing. On a front door, "no events"
  // as the first thing under the hero says the organization is dormant, and
  // both live tenants sit in this state. Failures are handled above, so
  // silence here always means zero, never broken.
  if (events.length === 0) return null;

  const len = events.length;

  // Only show ±2 side tiles when there are enough events (5+) to fill both sides symmetrically
  const maxVisible = len >= 5 ? 2 : 1;

  const getTileStyle = (index: number): React.CSSProperties => {
    const offset = ringOffset(activeIndex, index, len);
    const absOffset = Math.abs(offset);

    // Position tiles so edges stay close together regardless of scale.
    // Accumulate from center outward: half of active + gap + half of neighbor, etc.
    let x = 0;
    if (absOffset >= 1) {
      // Center of first neighbor = halfActive + gap + halfAdjacent
      x = (TILE_WIDTH * ACTIVE_SCALE) / 2 + TILE_GAP + (TILE_WIDTH * ADJACENT_SCALE) / 2;
    }
    if (absOffset >= 2) {
      // Center of second neighbor = first neighbor center + halfAdjacent + gap + halfFar
      x += (TILE_WIDTH * ADJACENT_SCALE) / 2 + TILE_GAP + (TILE_WIDTH * FAR_SCALE) / 2;
    }
    x *= Math.sign(offset);

    let scale: number;
    let opacity: number;
    let zIndex: number;

    if (absOffset === 0) {
      scale = ACTIVE_SCALE;
      opacity = 1;
      zIndex = 5;
    } else if (absOffset === 1) {
      scale = ADJACENT_SCALE;
      opacity = 0.7;
      zIndex = 3;
    } else if (absOffset <= maxVisible) {
      scale = FAR_SCALE;
      opacity = 0.4;
      zIndex = 1;
    } else {
      // Hidden: push off-screen in the direction they'll enter from
      scale = FAR_SCALE;
      opacity = 0;
      zIndex = 0;
    }

    return {
      transform: `translate(-50%, -50%) translateX(${x}px) scale(${scale})`,
      opacity,
      zIndex,
      pointerEvents: absOffset <= maxVisible ? 'auto' : 'none',
    } as React.CSSProperties;
  };

  // Resolve gallery URLs for an event
  const getGalleryUrls = (event: PublicEvent): string[] => {
    if (!Array.isArray(event.gallery) || event.gallery.length === 0) return [];
    return event.gallery
      .map((p) => getImageUrl(p))
      .filter((u): u is string => u !== null);
  };

  // Render the image area of a tile
  const renderTileImage = (event: PublicEvent) => {
    const urls = getGalleryUrls(event);
    if (urls.length === 0) {
      const primary = event.colorSettings?.primaryColor || 'var(--orange-primary)';
      const secondary = event.colorSettings?.secondaryColor || primary;
      const fallbackStyle = {
        '--event-primary': primary,
        '--event-secondary': secondary,
      } as React.CSSProperties;
      return (
        <div className="event-marquee__tile-placeholder" style={fallbackStyle}>
          <EventFallbackPattern seed={String(event._id)} />
        </div>
      );
    }
    return <GalleryCycler images={urls} alt={event.name} />;
  };

  return (
    <div
      className="event-marquee"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="event-marquee__header">
        <h2 className="event-marquee__title">{t('welcomePage.upcomingEvents')}</h2>
        {seeAllHref && (
          <Link to={seeAllHref} className="event-marquee__see-all">
            {seeAllLabel || t('welcomePage.seeAllEvents')}
          </Link>
        )}
      </div>

      <div className="event-marquee__viewport">
        {len > 1 && (
          <button
            className="event-marquee__arrow event-marquee__arrow--prev"
            onClick={goPrev}
            aria-label="Previous event"
          >
            ‹
          </button>
        )}

        <div className="event-marquee__stage">
          {events.map((event, index) => (
            <button
              key={event._id}
              type="button"
              className="event-marquee__tile"
              style={getTileStyle(index)}
              onClick={() => {
                if (index !== activeIndex) {
                  setActiveIndex(index);
                  return;
                }
                setModalSlug(event.publicSlug);
                setModalTitle(event.name);
              }}
            >
              {renderTileImage(event)}
              <div className="event-marquee__tile-body">
                <div className="event-marquee__tile-name">{event.name}</div>
                <div className="event-marquee__tile-date">{formatDate(event.startTime)}</div>
                {event.price != null && event.price > 0 && (
                  <div className="event-marquee__tile-price">
                    {t('welcomePage.eventPrice', { price: event.price })}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {len > 1 && (
          <button
            className="event-marquee__arrow event-marquee__arrow--next"
            onClick={goNext}
            aria-label="Next event"
          >
            ›
          </button>
        )}
      </div>

      {len > 1 && (
        <div className="event-marquee__dots">
          {events.map((_, index) => (
            <button
              key={index}
              className={`event-marquee__dot${index === activeIndex ? ' event-marquee__dot--active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Go to event ${index + 1}`}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={!!modalSlug}
        onClose={() => setModalSlug(null)}
        title={modalTitle}
        size="xlarge"
      >
        {modalSlug && <PublicEventDetailContent slug={modalSlug} />}
      </Modal>
    </div>
  );
};

export default EventMarquee;
